import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import styled from '@/styles';
import type { WorkerListItem } from '@/services/workersV2API';
import { formatDays, formatLongDate, stuckTone } from '../onboardingMappings';
import CardTooltip from './CardTooltip';

// Text colours are the dark shade of each tone so the pill reads at 4.5:1 on
// its tint (ticket WL-11).
const Pill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '3px 8px',
  borderRadius: '6px',
  border: '1px solid',
  fontSize: '13px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  variants: {
    tone: {
      amber: {
        backgroundColor: '#FFF6E0',
        borderColor: '#F1C76B',
        color: '#7A4B00',
      },
      red: {
        backgroundColor: '#FDECEC',
        borderColor: '#F0A6A6',
        color: '#A11A1A',
      },
      green: {
        backgroundColor: '#E7F4E6',
        borderColor: '#9FD397',
        color: '#1F6B14',
      },
    },
  },
});

const InfoButton = styled('button', {
  appearance: 'none',
  display: 'inline-flex',
  border: 'none',
  background: 'transparent',
  padding: 0,
  color: 'inherit',
  cursor: 'help',
});

const Dash = styled('span', { color: '#5A5A5A' });

type Props = {
  worker: WorkerListItem;
  // Background check / I-9 tabs: a cleared check has nothing to wait on.
  cleared?: boolean;
};

// "Stuck for" = days on the current pending status (current_status.elapsed_days,
// the same number the API's `progress` filter and `stuck_for` sort use). A
// worker past onboarding is "On track"; a disabled worker has no pending
// status and shows a dash.
const StuckForPill = ({ worker, cleared = false }: Props) => {
  const progress = worker.onboarding_progress;
  const days = worker.current_status?.elapsed_days;

  if (cleared) return <Dash data-testid={`stuckFor-${worker.id}`}>-</Dash>;
  // Before `completed`: a deactivated worker (listed by Status = Disabled)
  // keeps their finished onboarding, and read as a green "On track" (BH-31).
  if (worker.current_status?.state === 'disabled')
    return <Dash data-testid={`stuckFor-${worker.id}`}>—</Dash>;

  if (progress?.completed) {
    const last = worker.shifts?.last_completed_at;
    const tip = last
      ? `Last completed shift ${formatLongDate(last)}`
      : 'No completed shift yet';
    return (
      <Pill tone="green" data-testid={`stuckFor-${worker.id}`}>
        On track
        <CardTooltip title={tip}>
          {/* The pill sits in a clickable row: a click on the info icon
              shows its tooltip and must not open the side panel (review D-12). */}
          <InfoButton
            type="button"
            aria-label={tip}
            data-testid={`onTrackInfo-${worker.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </InfoButton>
        </CardTooltip>
      </Pill>
    );
  }

  if (days === null || days === undefined)
    return <Dash data-testid={`stuckFor-${worker.id}`}>—</Dash>;

  const tone = stuckTone(days);
  return (
    <Pill tone={tone} data-tone={tone} data-testid={`stuckFor-${worker.id}`}>
      {formatDays(days)}
    </Pill>
  );
};

export default StuckForPill;
